import {
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  Texture,
  type FederatedPointerEvent,
} from "pixi.js";
import { log } from "@freqhole/reliquary/utils";
import type { DocHandle } from "@automerge/automerge-repo";
import { resolveImagePropUrl, saveImageDataUrlAsBlobRef } from "../file-utils/image-prop-blob";
import type { SkeinTheme } from "../theme/skein-theme";
import { pickImageOrGifAsDataUrl } from "../widgets/gif-utils";

import { createSkeinInput } from "../widgets/skein-input";
import type { WidgetRegistry } from "../widgets/widget-registry";
import type {
  WidgetAction,
  WidgetDoc,
  WidgetFactory,
  WidgetPropDef,
} from "../widgets/widget-types";
import { TRANSPARENT_COLOR } from "../widgets/widget-types";
import type { WidgetEntry } from "./canvas-doc";
import type { CanvasStore } from "./canvas-store";
import { compactDoc, waitForQuietPeriod } from "./compact-doc";
import { computeDocHistoryStats, shouldOfferCompaction } from "../p2p/doc-history-stats";
import type { InputRouter } from "./input-router";
import type { WidgetManager } from "./widget-manager";

// layout constants
const DEFAULT_TRAY_WIDTH = 180;
const MIN_TRAY_WIDTH = 140;
const MAX_TRAY_WIDTH = 400;
const TRAY_GAP = 8;
const TRAY_PAD_H = 10;
const TRAY_PAD_V = 8;
const ROW_GAP = 10;
const FIELD_HEIGHT = 24;
const LABEL_FIELD_GAP = 3;
const RESIZE_HANDLE_WIDTH = 6;

/** `visibleWhen.value` matches either a single value, or (if an array) any
 *  value in the list. */
function matchesVisibleWhen(currentVal: unknown, expected: unknown): boolean {
  return Array.isArray(expected) ? expected.includes(currentVal) : currentVal === expected;
}

// preset color palette for the color picker
const COLOR_PALETTE = [
  // transparent + neutrals
  -1, 0x000000, 0x374151, 0x6b7280, 0xd1d5db, 0xffffff,
  // vivid
  0xef4444, 0xf97316, 0xeab308, 0x22c55e, 0x3b82f6, 0x8b5cf6,
  // pastel
  0xfca5a5, 0xfdba74, 0xfde047, 0x86efac, 0x93c5fd, 0xc4b5fd,
  // dark tones
  0x7f1d1d, 0x78350f, 0x713f12, 0x14532d, 0x1e3a5f, 0x3b0764,
];
const PALETTE_COLS = 6;
const SWATCH_SIZE = 20;
const SWATCH_GAP = 4;

/**
 * internal interface for a single prop control rendered inside the tray.
 */
interface PropControl {
  key: string;
  height: number;
  container: Container;
  /** update the control's displayed value (called on external doc changes) */
  update(value: unknown): void;
  /** adjust the control to a new field width (called during tray resize) */
  setWidth(fieldWidth: number): void;
  /** tear down the control */
  destroy(): void;
}

/**
 * property editing tray that appears to the right of the selected
 * widget in edit mode. renders editable controls for each WidgetPropDef
 * declared by the widget factory.
 *
 * only one tray is shown at a time. it lives in the world container
 * so it pans and zooms with widgets, at a very high zIndex so it
 * renders above all widget frames.
 *
 * the tray is horizontally resizable by dragging its right edge.
 *
 * supports string (keyboard-driven text input), number (+/- buttons),
 * boolean (toggle), color (swatch + palette popup), and select
 * (field + dropdown popup) controls.
 */
export class PropertyTray {
  readonly root: Container;

  private readonly theme: SkeinTheme;
  private readonly canvasElement: HTMLCanvasElement;
  private readonly inputRouter: InputRouter;
  private readonly widgetManager: WidgetManager;
  private readonly registry: WidgetRegistry;
  private readonly store: CanvasStore;

  private readonly bg: Graphics;
  private readonly header: Text;
  private readonly contentContainer: Container;
  private readonly resizeHandle: Graphics;

  private trayWidth = DEFAULT_TRAY_WIDTH;
  private currentWidgetId: string | null = null;
  /** the `docId` the tray was last built against for `currentWidgetId` --
   *  used by `checkForDocRotation()` to notice when another peer (or this
   *  one) compacts the doc for the widget currently shown in the tray. */
  private currentWidgetDocId: string | null = null;
  private controls: PropControl[] = [];
  private titleControl: PropControl | null = null;
  private deleteContainer: Container | null = null;
  private compactContainer: Container | null = null;
  /** visibility conditions for controls with visibleWhen */
  private controlVisibility = new Map<number, { key: string; value: unknown }>();
  private actionContainers: Container[] = [];
  private docUnsub: (() => void) | null = null;
  private unsubs: (() => void)[] = [];

  /**
   * function to stop the currently active keyboard editing session
   * inside a string control. null when no field is being edited.
   */
  private activeStopEditing: (() => void) | null = null;

  /**
   * function to close the currently active popup (color palette or
   * select dropdown). null when no popup is open.
   */
  private activePopupClose: (() => void) | null = null;

  // resize drag state
  private resizing = false;
  private resizeStartGlobalX = 0;
  private resizeStartWidth = 0;

  /** current viewport zoom, kept in sync via setZoom() so the tray can
   *  counter-scale itself and render at a constant on-screen size instead
   *  of shrinking/growing with the world (see setZoom() below). */
  private currentZoom = 1;

  constructor(
    world: Container,
    theme: SkeinTheme,
    canvasElement: HTMLCanvasElement,
    inputRouter: InputRouter,
    widgetManager: WidgetManager,
    store: CanvasStore,
    registry: WidgetRegistry
  ) {
    this.theme = theme;
    this.canvasElement = canvasElement;
    this.inputRouter = inputRouter;
    this.widgetManager = widgetManager;
    this.registry = registry;
    this.store = store;

    // root container — hidden until a widget is selected
    this.root = new Container();
    this.root.visible = false;
    this.root.zIndex = 99999;
    this.root.eventMode = "static";
    this.root.interactiveChildren = true;
    this.root.sortableChildren = true;

    // swallow pointer events so they don't reach the world/stage.
    // also close any open popup when clicking the tray background.
    this.root.on("pointerdown", (e) => {
      e.stopPropagation();
      this.closeActivePopup();
    });

    // tray background
    this.bg = new Graphics();
    this.root.addChild(this.bg);

    // header showing the widget name
    this.header = new Text({
      text: "",
      resolution: theme.textResolution,
      style: {
        fontFamily: theme.fontFamily,
        fontSize: theme.fontSizeSmall,
        fill: theme.frameHeaderText,
        fontWeight: "bold",
      },
    });
    this.header.x = TRAY_PAD_H;
    this.header.y = TRAY_PAD_V;
    this.header.eventMode = "none";
    this.root.addChild(this.header);

    // container for prop control rows
    this.contentContainer = new Container();
    this.contentContainer.x = TRAY_PAD_H;
    this.root.addChild(this.contentContainer);

    // resize handle on the right edge
    this.resizeHandle = new Graphics();
    this.resizeHandle.eventMode = "static";
    this.resizeHandle.cursor = "ew-resize";
    this.root.addChild(this.resizeHandle);
    this.setupResizeHandle();

    world.addChild(this.root);

    // subscribe to selection and mode changes
    this.unsubs.push(inputRouter.onSelectionChange(() => this.refresh()));

    // reposition tray when the store changes (widget moved/resized), and
    // check whether the selected widget's own doc got rotated (e.g. another
    // peer compacted it) — see checkForDocRotation()'s own doc comment.
    this.unsubs.push(
      store.onChange(() => {
        this.repositionIfNeeded();
        this.checkForDocRotation();
      })
    );
  }

  // ---------------------------------------------------------------------------
  // resize handle
  // ---------------------------------------------------------------------------

  private setupResizeHandle(): void {
    this.resizeHandle.on("pointerdown", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      this.resizing = true;
      this.resizeStartGlobalX = e.global.x;
      this.resizeStartWidth = this.trayWidth;
    });

    this.resizeHandle.on("globalpointermove", (e: FederatedPointerEvent) => {
      if (!this.resizing) return;
      const zoom = this.root.parent?.scale.x ?? 1;
      const dx = (e.global.x - this.resizeStartGlobalX) / zoom;
      const newWidth = Math.round(
        Math.max(MIN_TRAY_WIDTH, Math.min(MAX_TRAY_WIDTH, this.resizeStartWidth + dx))
      );
      if (newWidth !== this.trayWidth) {
        this.trayWidth = newWidth;
        this.applyWidth();
      }
    });

    this.resizeHandle.on("pointerup", () => {
      this.resizing = false;
    });

    this.resizeHandle.on("pointerupoutside", () => {
      this.resizing = false;
    });
  }

  /**
   * propagate the current tray width to all controls and redraw chrome.
   */
  private applyWidth(): void {
    const fieldWidth = this.trayWidth - TRAY_PAD_H * 2;
    this.titleControl?.setWidth(fieldWidth);
    for (const control of this.controls) {
      control.setWidth(fieldWidth);
    }
    this.drawBackground();
    this.drawResizeHandle();
  }

  private drawResizeHandle(): void {
    const contentBottom = this.contentContainer.y + this.contentContainer.height;
    const totalHeight = contentBottom + TRAY_PAD_V;

    this.resizeHandle.clear();
    // transparent hit area on the right edge
    this.resizeHandle.rect(
      this.trayWidth - RESIZE_HANDLE_WIDTH / 2,
      0,
      RESIZE_HANDLE_WIDTH,
      totalHeight
    );
    this.resizeHandle.fill({ color: 0x000000, alpha: 0 });
    // subtle visual indicator line
    this.resizeHandle.rect(this.trayWidth - 1, 8, 1, Math.max(totalHeight - 16, 8));
    this.resizeHandle.fill({ color: this.theme.frameBorder, alpha: 0.5 });
  }

  // ---------------------------------------------------------------------------
  // lifecycle
  // ---------------------------------------------------------------------------

  /**
   * re-evaluate whether the tray should be shown, hidden, or rebuilt.
   */
  private refresh(): void {
    const selectedId = this.inputRouter.selectedWidgetId;
    if (!selectedId) {
      this.hide();
      return;
    }

    // viewers get no property tray at all — there's nothing they're allowed
    // to edit (title, prop fields, delete). this is a UI convention, not a
    // security boundary — see skein-runtime-plan.md's ACL trust-model notes.
    if (this.store.isLocalViewer()) {
      this.hide();
      return;
    }

    // if the same widget is still selected, don't rebuild
    if (selectedId === this.currentWidgetId && this.root.visible) {
      return;
    }

    const live = this.widgetManager.getLiveWidgets().get(selectedId);
    if (!live) {
      this.hide();
      return;
    }

    if (live.crashed) {
      // crashed widgets still get a minimal tray with title + delete button
      this.showCrashed(selectedId, live.entry);
      this.positionNextTo(live.frame.root.x, live.frame.root.y, live.entry.width);
      return;
    }

    const factory = this.registry.get(live.entry.type);
    if (!factory) {
      this.hide();
      return;
    }

    this.show(
      selectedId,
      factory,
      live.widgetDoc,
      live.entry,
      live.ctrl.widgetActions,
      live.ctrl.editableProps,
      live.ctrl.widgetInfoRows
    );
    this.positionNextTo(live.frame.root.x, live.frame.root.y, live.entry.width);
  }

  /**
   * detects when the currently-shown widget's own doc has been rotated
   * (e.g. this peer or another peer compacted it via the "compact this
   * doc" button) and rebuilds the tray against the fresh doc facade once
   * the remount actually completes. without this, the tray would keep
   * operating on a stale, orphaned DocHandle indefinitely -- its prop
   * controls' doc subscription (`docUnsub`, set up in `show()`) is bound
   * to the OLD facade captured at that time, and would simply go quiet
   * once nobody writes to the abandoned doc anymore, silently discarding
   * any further edits made through it.
   */
  private checkForDocRotation(): void {
    if (!this.currentWidgetId) return;
    const widgetId = this.currentWidgetId;
    const currentDocId = this.store.doc().widgets[widgetId]?.docId ?? null;
    if (currentDocId === this.currentWidgetDocId) return;

    this.widgetManager.onNextMount(widgetId, () => {
      // only rebuild if this widget is still the one selected by the time
      // the remount finishes -- if the user selected something else in
      // the meantime, refresh() already handled that separately.
      if (this.currentWidgetId !== widgetId) return;
      this.currentWidgetId = null; // defeat refresh()'s same-widget fast path
      this.refresh();
    });
  }

  /**
   * build and display the tray for a specific widget.
   */
  private show(
    widgetId: string,
    factory: WidgetFactory,
    doc: WidgetDoc<any> | null,
    entry: WidgetEntry,
    widgetActions?: WidgetAction[],
    controllerProps?: WidgetPropDef[],
    widgetInfoRows?: () => Array<{ label: string; value: string }>
  ): void {
    // tear down any previous tray state
    this.clearControls();

    this.currentWidgetId = widgetId;
    this.currentWidgetDocId = entry.docId ?? null;
    this.header.text = factory.metadata.name;

    // position the content below the header
    this.contentContainer.y = Math.round(this.header.y + this.header.height + ROW_GAP);

    const fieldWidth = this.trayWidth - TRAY_PAD_H * 2;
    let y = 0;

    // widget-specific prop controls (only if doc exists and factory has editable props)
    const props = controllerProps ?? factory.editableProps ?? [];

    // title field — always shown, EXCEPT when the widget already declares its
    // own "title" editable prop (e.g. the file widget's doc-backed title,
    // which feeds the bin label) — showing both would be a duplicate input.
    const hasOwnTitleProp = props.some((p) => p.key === "title");
    if (!hasOwnTitleProp) {
      this.titleControl = this.createTitleControl(widgetId, entry.title ?? "", fieldWidth);
      this.contentContainer.addChild(this.titleControl.container);
      this.titleControl.container.y = Math.round(y);
      y += this.titleControl.height + ROW_GAP;
    }

    if (doc && props.length) {
      for (let i = 0; i < props.length; i++) {
        const prop = props[i];
        const control = this.createControl(prop, doc, fieldWidth);
        this.contentContainer.addChild(control.container);
        this.controls.push(control);

        // track conditional visibility
        if (prop.visibleWhen) {
          this.controlVisibility.set(i, prop.visibleWhen);
          const currentVal = doc.current[prop.visibleWhen.key];
          if (!matchesVisibleWhen(currentVal, prop.visibleWhen.value)) {
            control.container.visible = false;
          }
        }

        // layout only visible controls
        if (control.container.visible) {
          control.container.y = Math.round(y);
          y += control.height + ROW_GAP;
        }
      }

      // subscribe to doc changes so controls stay in sync with remote edits
      this.docUnsub = doc.on("change", (state: Record<string, unknown>) => {
        let needsRelayout = false;
        for (let i = 0; i < this.controls.length; i++) {
          const control = this.controls[i];
          control.update(state[control.key]);

          const vis = this.controlVisibility.get(i);
          if (vis) {
            const shouldBeVisible = matchesVisibleWhen(state[vis.key], vis.value);
            if (control.container.visible !== shouldBeVisible) {
              control.container.visible = shouldBeVisible;
              needsRelayout = true;
            }
          }
        }
        if (needsRelayout) {
          this.relayoutControls();
        }
      });
    }

    // read-only info rows (e.g. the file widget's "who has this file" list)
    const infoRows = widgetInfoRows?.() ?? [];
    if (infoRows.length) {
      y += ROW_GAP;
      for (const row of infoRows) {
        const rowContainer = new Container();
        const rowText = new Text({
          text: row.label ? `${row.label}: ${row.value}` : row.value,
          style: {
            fontFamily: "system-ui, sans-serif",
            fontSize: 10,
            fill: 0x9999aa,
            align: "left",
            wordWrap: true,
            wordWrapWidth: fieldWidth,
          },
          resolution: 2,
        });
        rowContainer.addChild(rowText);
        this.contentContainer.addChild(rowContainer);
        rowContainer.y = Math.round(y);
        // reuse the action-container cleanup path — clearControls destroys these
        this.actionContainers.push(rowContainer);
        y += Math.max(14, rowText.height) + 4;
      }
    }

    // widget actions (e.g. "tidy" in the bin widget)
    if (widgetActions?.length) {
      y += ROW_GAP;
      for (const action of widgetActions) {
        const btn = this.createWidgetActionButton(action, fieldWidth);
        this.contentContainer.addChild(btn);
        btn.y = Math.round(y);
        this.actionContainers.push(btn);
        y += FIELD_HEIGHT + ROW_GAP;
      }
    }

    // "compact this doc" — only for stateful widgets with a real automerge
    // DocHandle (not stateless widgets or a non-automerge doc override like
    // SqliteSocialDoc); shows an empty container (no button) otherwise, same
    // pattern as createDeleteButton's isSingleton early-return.
    const compact = this.createCompactButton(widgetId, this.widgetManager.getWidgetDocHandle(widgetId), fieldWidth);
    this.compactContainer = compact.container;
    this.contentContainer.addChild(this.compactContainer);
    if (compact.height > 0) {
      y += ROW_GAP;
      this.compactContainer.y = Math.round(y);
      y += compact.height;
    }

    // delete button at the bottom (skip for singletons)
    const isSingleton = factory.metadata.singleton === true;
    this.deleteContainer = this.createDeleteButton(widgetId, isSingleton, fieldWidth);
    this.contentContainer.addChild(this.deleteContainer);
    if (!isSingleton) {
      y += ROW_GAP;
      this.deleteContainer.y = Math.round(y);
    }

    this.drawBackground();
    this.drawResizeHandle();
    this.root.visible = true;
  }

  /**
   * show a minimal tray for a crashed widget — just the title + delete button.
   * no editable props or widget actions since the widget failed to mount.
   */
  private showCrashed(widgetId: string, entry: WidgetEntry): void {
    this.clearControls();

    this.currentWidgetId = widgetId;
    this.currentWidgetDocId = entry.docId ?? null;

    // try to get a display name from the factory, fall back to the entry type
    const factory = this.registry.get(entry.type);
    this.header.text = factory?.metadata.name ?? entry.type;

    this.contentContainer.y = Math.round(this.header.y + this.header.height + ROW_GAP);

    const fieldWidth = this.trayWidth - TRAY_PAD_H * 2;
    let y = 0;

    // title field — still useful for identification even if crashed
    this.titleControl = this.createTitleControl(widgetId, entry.title ?? "", fieldWidth);
    this.contentContainer.addChild(this.titleControl.container);
    this.titleControl.container.y = Math.round(y);
    y += this.titleControl.height + ROW_GAP;

    // delete button — the main reason we show this tray
    const isSingleton = factory?.metadata.singleton === true;
    this.deleteContainer = this.createDeleteButton(widgetId, isSingleton, fieldWidth);
    this.contentContainer.addChild(this.deleteContainer);
    if (!isSingleton) {
      y += ROW_GAP;
      this.deleteContainer.y = Math.round(y);
    }

    this.drawBackground();
    this.drawResizeHandle();
    this.root.visible = true;
  }

  /**
   * hide the tray and tear down controls.
   */
  hide(): void {
    // release keyboard if a string field is being edited
    if (this.activeStopEditing) {
      this.activeStopEditing();
      this.activeStopEditing = null;
    }

    this.closeActivePopup();
    this.clearControls();
    this.currentWidgetId = null;
    this.currentWidgetDocId = null;
    this.root.visible = false;
  }

  /**
   * re-compute y positions for controls after visibility changes.
   */
  private relayoutControls(): void {
    let y = 0;
    // title control is always first
    if (this.titleControl) {
      this.titleControl.container.y = Math.round(y);
      y += this.titleControl.height + ROW_GAP;
    }
    for (const control of this.controls) {
      if (!control.container.visible) continue;
      control.container.y = Math.round(y);
      y += control.height + ROW_GAP;
    }
    // widget action buttons
    if (this.actionContainers.length) {
      y += ROW_GAP;
      for (const btn of this.actionContainers) {
        btn.y = Math.round(y);
        y += FIELD_HEIGHT + ROW_GAP;
      }
    }
    // compact button, if shown
    if (this.compactContainer && this.compactContainer.children.length > 0) {
      y += ROW_GAP;
      this.compactContainer.y = Math.round(y);
      y += this.compactContainer.getBounds().height + ROW_GAP;
    }
    // delete button at the bottom
    if (this.deleteContainer && this.deleteContainer.children.length > 0) {
      y += ROW_GAP;
      this.deleteContainer.y = Math.round(y);
    }
    this.drawBackground();
  }

  /**
   * tear down controls and doc subscription without hiding the root.
   */
  private clearControls(): void {
    if (this.docUnsub) {
      this.docUnsub();
      this.docUnsub = null;
    }

    if (this.titleControl) {
      this.titleControl.destroy();
      this.titleControl = null;
    }

    for (const control of this.controls) {
      control.destroy();
    }
    this.controls = [];
    this.controlVisibility.clear();

    for (const btn of this.actionContainers) {
      btn.destroy({ children: true });
    }
    this.actionContainers = [];

    if (this.deleteContainer) {
      this.deleteContainer.destroy({ children: true });
      this.deleteContainer = null;
    }

    if (this.compactContainer) {
      this.compactContainer.destroy({ children: true });
      this.compactContainer = null;
    }

    this.contentContainer.removeChildren();
  }

  private positionNextTo(frameX: number, frameY: number, frameWidth: number): void {
    // the tray's own dimensions (trayWidth, gaps, header height) are all
    // meant to be constant *screen* pixels (see setZoom()), but frameX/
    // frameWidth are genuine world-space coordinates — convert screen-pixel
    // offsets into world units so the tray still lands in the right spot.
    const invZoom = 1 / this.currentZoom;

    // when the widget is maximized, the tray would normally be positioned
    // off-screen (to the right of the full-viewport widget). instead,
    // overlay it inside the viewport on the right side.
    if (this.widgetManager.isMaximized) {
      const margin = 8 * invZoom;
      this.root.x = Math.round(frameX + frameWidth - this.trayWidth * invZoom - margin);
      this.root.y = Math.round(frameY + margin);
      return;
    }

    this.root.x = Math.round(frameX + frameWidth + TRAY_GAP * invZoom);
    this.root.y = Math.round(frameY - this.theme.frameHeaderHeight * invZoom);
  }

  /**
   * the tray lives in the world container so it pans with the selected
   * widget, but it should never get harder to read/tap just because the
   * canvas is zoomed out — counter-scale it by 1/zoom so its rendered
   * (screen) size stays constant regardless of viewport zoom, same idea as
   * WidgetFrame's resize-handle compensation. call whenever the viewport's
   * zoom changes (see init.ts's viewport.onZoomChange wiring).
   */
  setZoom(zoom: number): void {
    if (this.currentZoom === zoom) return;
    this.currentZoom = zoom;
    this.root.scale.set(1 / zoom);
    this.repositionIfNeeded();
  }

  /**
   * re-evaluate this tray's position against the currently selected widget's
   * *live* frame position. safe to call frequently (e.g. on every tick of
   * a drag, before the drag commits to the store) — no-ops if the tray
   * isn't visible or the widget isn't currently selected. public so
   * `init.ts` can wire it to `WidgetManager.onLiveMove()`, keeping the tray
   * glued to the widget during a drag instead of only updating once the
   * drag ends and `store.onChange()` fires.
   */
  repositionIfNeeded(): void {
    if (!this.currentWidgetId || !this.root.visible) return;

    // role can change while the tray is open (an admin downgrades the local
    // peer to viewer) — hide immediately rather than waiting for the next
    // selection change.
    if (this.store.isLocalViewer()) {
      this.hide();
      return;
    }

    const live = this.widgetManager.getLiveWidgets().get(this.currentWidgetId);
    if (!live) {
      this.hide();
      return;
    }

    this.positionNextTo(live.frame.root.x, live.frame.root.y, live.entry.width);
  }

  private drawBackground(): void {
    const contentBottom = this.contentContainer.y + this.contentContainer.height;
    const totalHeight = contentBottom + TRAY_PAD_V;

    this.bg.clear();
    this.bg.roundRect(0, 0, this.trayWidth, totalHeight, 6);
    this.bg.fill({ color: this.theme.toolbarBg, alpha: 0.96 });
    this.bg.stroke({ color: this.theme.toolbarBorder, width: 1 });
  }

  destroy(): void {
    this.hide();
    for (const unsub of this.unsubs) {
      unsub();
    }
    this.unsubs = [];
    this.root.destroy({ children: true });
  }

  // ---------------------------------------------------------------------------
  // popup management
  // ---------------------------------------------------------------------------

  /**
   * close the currently open popup (color palette or select dropdown).
   */
  private closeActivePopup(): void {
    if (this.activePopupClose) {
      const close = this.activePopupClose;
      this.activePopupClose = null;
      close();
    }
  }

  // ---------------------------------------------------------------------------
  // control factory
  // ---------------------------------------------------------------------------

  private createControl(prop: WidgetPropDef, doc: WidgetDoc<any>, fieldWidth: number): PropControl {
    const currentValue = doc.current[prop.key];
    const onChange = (value: unknown) => {
      doc.change((draft: Record<string, unknown>) => {
        draft[prop.key] = value;
      });
    };

    switch (prop.type) {
      case "string":
        return this.createStringControl(
          prop,
          String(currentValue ?? prop.default ?? ""),
          onChange as (v: string) => void,
          fieldWidth
        );
      case "number":
        return this.createNumberControl(
          prop,
          Number(currentValue ?? prop.default ?? 0),
          onChange as (v: number) => void,
          fieldWidth
        );
      case "boolean":
        return this.createBooleanControl(
          prop,
          Boolean(currentValue ?? prop.default ?? false),
          onChange as (v: boolean) => void,
          fieldWidth
        );
      case "color":
        return this.createColorControl(
          prop,
          Number(currentValue ?? prop.default ?? 0x000000),
          onChange as (v: number) => void,
          fieldWidth
        );
      case "select":
        return this.createSelectControl(
          prop,
          String(currentValue ?? prop.default ?? ""),
          onChange as (v: string) => void,
          fieldWidth
        );
      case "image":
        return this.createImageControl(
          prop,
          String(currentValue ?? prop.default ?? ""),
          onChange as (v: string) => void,
          fieldWidth
        );
    }
  }

  // ---------------------------------------------------------------------------
  // shared helpers
  // ---------------------------------------------------------------------------

  /**
   * create a label Text for a prop row.
   */
  private createLabel(text: string): Text {
    const label = new Text({
      text,
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall - 1,
        fill: 0x808080,
      },
    });
    label.eventMode = "none";
    return label;
  }

  /**
   * draw a standard field background rectangle.
   */
  private drawFieldBg(g: Graphics, fieldY: number, fieldWidth: number, highlight: boolean): void {
    g.clear();
    g.roundRect(0, fieldY, fieldWidth, FIELD_HEIGHT, 4);
    g.fill({ color: highlight ? 0x1a1a2e : 0x141414 });
    g.stroke({
      color: highlight ? this.theme.accent : this.theme.frameBorder,
      width: 1,
    });
  }

  // ---------------------------------------------------------------------------
  // title control (edits entry.title on the canvas store)
  // ---------------------------------------------------------------------------

  /** create a styled button for a widget action (shown between props and delete) */
  private createWidgetActionButton(action: WidgetAction, fieldWidth: number): Container {
    const container = new Container();
    const btnHeight = FIELD_HEIGHT;

    const bg = new Graphics();
    bg.roundRect(0, 0, fieldWidth, btnHeight, 4);
    bg.fill({ color: 0x2a2a2a });
    bg.eventMode = "static";
    bg.cursor = "pointer";
    container.addChild(bg);

    const label = new Text({
      text: action.label,
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: this.theme.frameHeaderText,
      },
    });
    label.anchor.set(0.5, 0.5);
    label.x = fieldWidth / 2;
    label.y = btnHeight / 2;
    label.eventMode = "none";
    container.addChild(label);

    bg.on("pointerenter", () => {
      bg.clear();
      bg.roundRect(0, 0, fieldWidth, btnHeight, 4);
      bg.fill({ color: 0x3a3a3a });
    });
    bg.on("pointerleave", () => {
      bg.clear();
      bg.roundRect(0, 0, fieldWidth, btnHeight, 4);
      bg.fill({ color: 0x2a2a2a });
    });
    bg.on("pointertap", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      action.onClick();
    });

    return container;
  }

  private createTitleControl(
    widgetId: string,
    currentTitle: string,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    const label = this.createLabel("title");
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);

    const handle = createSkeinInput({
      canvasElement: this.canvasElement,
      width: fieldWidth,
      height: FIELD_HEIGHT,
      value: currentTitle,
      onChange: (value: string) => {
        this.store.setWidgetTitle(widgetId, value);
      },
      fontSize: this.theme.fontSizeSmall,
      fontFamily: this.theme.fontFamily,
      textColor: this.theme.frameHeaderText,
      bgColor: 0x141414,
      borderColor: this.theme.frameBorder,
      borderActiveColor: this.theme.accent,
      cornerRadius: 4,
    });

    handle.input.y = fieldY;
    container.addChild(handle.input);

    // close any active popup when the input is clicked/focused
    handle.input.on("pointertap", () => {
      this.closeActivePopup();
    });

    const totalHeight = fieldY + FIELD_HEIGHT;

    return {
      key: "__title__",
      height: totalHeight,
      container,
      update(value: unknown) {
        if (!handle.isEditing) {
          handle.value = String(value ?? "");
        }
      },
      setWidth(fw: number) {
        handle.setWidth(fw);
      },
      destroy() {
        handle.blur();
        handle.destroy();
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // string control
  // ---------------------------------------------------------------------------

  private createStringControl(
    prop: WidgetPropDef,
    initialValue: string,
    onChange: (value: string) => void,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);

    const handle = createSkeinInput({
      canvasElement: this.canvasElement,
      width: fieldWidth,
      height: FIELD_HEIGHT,
      value: initialValue,
      onChange,
      // theme overrides to match property tray styling
      fontSize: this.theme.fontSizeSmall,
      fontFamily: this.theme.fontFamily,
      textColor: this.theme.frameHeaderText,
      bgColor: 0x141414,
      borderColor: this.theme.frameBorder,
      borderActiveColor: this.theme.accent,
      cornerRadius: 4,
    });

    handle.input.y = fieldY;
    container.addChild(handle.input);

    // close any active popup when the input is clicked/focused
    handle.input.on("pointertap", () => {
      this.closeActivePopup();
    });

    const totalHeight = fieldY + FIELD_HEIGHT;

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(value: unknown) {
        if (!handle.isEditing) {
          handle.value = String(value ?? "");
        }
      },
      setWidth(fw: number) {
        handle.setWidth(fw);
      },
      destroy() {
        handle.blur();
        handle.destroy();
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // number control
  // ---------------------------------------------------------------------------

  private createNumberControl(
    prop: WidgetPropDef,
    initialValue: number,
    onChange: (value: number) => void,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);
    let currentFieldWidth = fieldWidth;
    let currentValue = initialValue;

    const btnWidth = FIELD_HEIGHT;

    const lo = prop.min ?? 1;
    const hi = prop.max ?? 100;
    const step = prop.step ?? 1;
    const stepDecimals = (() => {
      const s = String(step);
      const dot = s.indexOf(".");
      return dot === -1 ? 0 : s.length - dot - 1;
    })();
    const clamp = (n: number) => {
      const snapped = Number.isFinite(n) ? Math.round(n / step) * step : lo;
      const rounded = Number(snapped.toFixed(stepDecimals));
      return Math.max(lo, Math.min(hi, rounded));
    };

    // minus button
    const minusBtn = this.createNumberButton("\u2212");
    container.addChild(minusBtn.container);

    // plus button
    const plusBtn = this.createNumberButton("+");
    container.addChild(plusBtn.container);

    // text-editable number field between the buttons
    const inputWidth = () => currentFieldWidth - btnWidth * 2;
    const inputHandle = createSkeinInput({
      canvasElement: this.canvasElement,
      width: inputWidth(),
      height: FIELD_HEIGHT,
      value: String(currentValue),
      align: "center",
      fontSize: this.theme.fontSizeSmall,
      fontFamily: this.theme.fontFamily,
      textColor: this.theme.frameHeaderText,
      bgColor: 0x141414,
      borderColor: this.theme.frameBorder,
      borderActiveColor: this.theme.accent,
      cornerRadius: 4,
      onEnter: (val: string) => {
        currentValue = clamp(parseFloat(val));
        inputHandle.value = String(currentValue);
        onChange(currentValue);
      },
      onChange: (val: string) => {
        // live update only if it's a valid number
        const n = parseFloat(val);
        if (!isNaN(n)) {
          currentValue = clamp(n);
          onChange(currentValue);
        }
      },
    });
    container.addChild(inputHandle.input);

    const layout = () => {
      minusBtn.container.x = 0;
      minusBtn.container.y = fieldY;
      minusBtn.setSize(btnWidth, FIELD_HEIGHT);

      inputHandle.input.x = btnWidth;
      inputHandle.input.y = fieldY;
      inputHandle.setWidth(inputWidth());

      plusBtn.container.x = currentFieldWidth - btnWidth;
      plusBtn.container.y = fieldY;
      plusBtn.setSize(btnWidth, FIELD_HEIGHT);
    };
    layout();

    /** wire press-and-hold repeat behavior on a number button.
     *  ramps up speed the longer the button is held. */
    const wireHoldRepeat = (btn: Container, delta: number) => {
      let holdTimer: ReturnType<typeof setTimeout> | null = null;
      let repeatTimer: ReturnType<typeof setTimeout> | null = null;
      let holdStartTime = 0;

      const applyDelta = () => {
        currentValue = clamp(currentValue + delta);
        inputHandle.value = String(currentValue);
        onChange(currentValue);
      };

      const getInterval = (): number => {
        const elapsed = Date.now() - holdStartTime;
        if (elapsed > 3000) return 30;
        if (elapsed > 1500) return 80;
        return 150;
      };

      const scheduleNext = () => {
        repeatTimer = setTimeout(() => {
          applyDelta();
          scheduleNext();
        }, getInterval());
      };

      const stopRepeat = () => {
        if (holdTimer !== null) {
          clearTimeout(holdTimer);
          holdTimer = null;
        }
        if (repeatTimer !== null) {
          clearTimeout(repeatTimer);
          repeatTimer = null;
        }
      };

      btn.on("pointerdown", (e: FederatedPointerEvent) => {
        e.stopPropagation();
        this.closeActivePopup();

        // immediate single increment
        applyDelta();

        // start hold timer -- after 400ms, begin repeating
        holdStartTime = Date.now();
        holdTimer = setTimeout(() => {
          holdTimer = null;
          scheduleNext();
        }, 400);
      });

      btn.on("pointerup", stopRepeat);
      btn.on("pointerupoutside", stopRepeat);
      btn.on("pointercancel", stopRepeat);
      btn.on("pointerleave", stopRepeat);
    };

    wireHoldRepeat(minusBtn.container, -step);
    wireHoldRepeat(plusBtn.container, step);

    const totalHeight = fieldY + FIELD_HEIGHT;

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(value: unknown) {
        currentValue = clamp(Number(value) || 1);
        inputHandle.value = String(currentValue);
      },
      setWidth(fw: number) {
        currentFieldWidth = fw;
        layout();
      },
      destroy() {
        inputHandle.destroy();
        container.destroy({ children: true });
      },
    };
  }

  private createNumberButton(labelStr: string): {
    container: Container;
    setSize: (w: number, h: number) => void;
  } {
    const btnContainer = new Container();
    btnContainer.eventMode = "static";
    btnContainer.cursor = "pointer";

    const bg = new Graphics();
    btnContainer.addChild(bg);

    const text = new Text({
      text: labelStr,
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: this.theme.frameHeaderText,
      },
    });
    text.anchor.set(0.5);
    // transparent to pointer events so clicks reach the button bg
    text.eventMode = "none";
    btnContainer.addChild(text);

    let currentW = 0;
    let currentH = 0;

    const draw = (hover: boolean) => {
      bg.clear();
      bg.roundRect(0, 0, currentW, currentH, 4);
      bg.fill({ color: hover ? this.theme.accent : 0x1a1a1a, alpha: hover ? 0.2 : 1 });
    };

    btnContainer.on("pointerover", () => draw(true));
    btnContainer.on("pointerout", () => draw(false));

    const setSize = (w: number, h: number) => {
      currentW = w;
      currentH = h;
      text.x = Math.round(w / 2);
      text.y = Math.round(h / 2);
      draw(false);
    };

    return { container: btnContainer, setSize };
  }

  // ---------------------------------------------------------------------------
  // boolean control
  // ---------------------------------------------------------------------------

  private createBooleanControl(
    prop: WidgetPropDef,
    initialValue: boolean,
    onChange: (value: boolean) => void,
    _fieldWidth: number
  ): PropControl {
    const container = new Container();

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);
    const trackWidth = 36;
    const trackHeight = 20;
    const thumbRadius = 7;
    const thumbPad = 3;

    let value = initialValue;

    // track and thumb are purely decorative (eventMode "none") — a single
    // hit area on the parent container below handles all clicks uniformly,
    // so the toggle's "dot" is exactly as clickable as the rest of the row
    // instead of depending on which shape happens to be hit-tested first.
    const track = new Graphics();
    track.eventMode = "none";
    container.addChild(track);

    const thumb = new Graphics();
    thumb.eventMode = "none";
    container.addChild(thumb);

    const drawToggle = () => {
      track.clear();
      track.roundRect(0, fieldY, trackWidth, trackHeight, trackHeight / 2);
      track.fill({ color: value ? this.theme.accent : 0x2a2a2a });

      thumb.clear();
      const thumbX = value ? trackWidth - thumbRadius - thumbPad : thumbRadius + thumbPad;
      const thumbY = fieldY + trackHeight / 2;
      thumb.circle(thumbX, thumbY, thumbRadius);
      thumb.fill({ color: 0xffffff });
    };

    drawToggle();

    const totalHeight = fieldY + trackHeight;

    // one hit area covering the label, the toggle, and the padding around
    // both — clicking anywhere on the row (including exactly on the thumb)
    // toggles the value.
    const HIT_PAD = 10;
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new Rectangle(
      -HIT_PAD,
      -HIT_PAD,
      Math.max(trackWidth, label.width) + HIT_PAD * 2,
      totalHeight + HIT_PAD * 2
    );
    container.on("pointerdown", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      this.closeActivePopup();
      value = !value;
      onChange(value);
      drawToggle();
    });

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(newValue: unknown) {
        value = Boolean(newValue);
        drawToggle();
      },
      setWidth() {
        // toggle is fixed width — nothing to do
      },
      destroy() {
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // color control
  // ---------------------------------------------------------------------------

  private createColorControl(
    prop: WidgetPropDef,
    initialValue: number,
    onChange: (value: number) => void,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);
    let currentFieldWidth = fieldWidth;
    let currentColor = initialValue;

    // field background
    const fieldBg = new Graphics();
    container.addChild(fieldBg);

    // color swatch
    const swatchSize = FIELD_HEIGHT - 6;
    const swatch = new Graphics();
    container.addChild(swatch);

    // hex label
    const hexText = new Text({
      text: formatHex(currentColor),
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall - 1,
        fill: this.theme.frameHeaderText,
      },
    });
    hexText.eventMode = "none";
    container.addChild(hexText);

    // palette popup container (hidden by default)
    let paletteContainer: Container | null = null;
    // live DOM color input — created when the rainbow swatch is clicked,
    // lives until the palette closes or a color is picked.
    let liveColorInput: HTMLInputElement | null = null;

    const drawSwatch = () => {
      swatch.clear();
      const x0 = 4,
        y0 = fieldY + 3;
      if (currentColor === TRANSPARENT_COLOR) {
        // checkerboard pattern for transparent
        const half = Math.floor(swatchSize / 2);
        // light squares
        swatch.rect(x0, y0, half, half);
        swatch.fill({ color: 0xcccccc });
        swatch.rect(x0 + half, y0 + half, swatchSize - half, swatchSize - half);
        swatch.fill({ color: 0xcccccc });
        // dark squares
        swatch.rect(x0 + half, y0, swatchSize - half, half);
        swatch.fill({ color: 0x888888 });
        swatch.rect(x0, y0 + half, half, swatchSize - half);
        swatch.fill({ color: 0x888888 });
        // border
        swatch.roundRect(x0, y0, swatchSize, swatchSize, 3);
        swatch.stroke({ color: 0x555555, width: 1 });
      } else {
        swatch.roundRect(x0, y0, swatchSize, swatchSize, 3);
        swatch.fill({ color: currentColor });
        swatch.stroke({ color: 0x555555, width: 1 });
      }
    };

    const drawFieldLayout = () => {
      this.drawFieldBg(fieldBg, fieldY, currentFieldWidth, false);
      drawSwatch();
      hexText.x = 4 + swatchSize + 6;
      hexText.y = fieldY + Math.round((FIELD_HEIGHT - hexText.height) / 2);
    };
    drawFieldLayout();

    const closePalette = () => {
      // remove any live color input — this also closes the native picker
      if (liveColorInput && document.body.contains(liveColorInput)) {
        document.body.removeChild(liveColorInput);
      }
      liveColorInput = null;
      if (paletteContainer) {
        if (paletteContainer.parent) {
          paletteContainer.parent.removeChild(paletteContainer);
        }
        paletteContainer.destroy({ children: true });
        paletteContainer = null;
      }
      if (this.activePopupClose === closePalette) {
        this.activePopupClose = null;
      }
    };

    const openPalette = () => {
      // close any other popup first
      this.closeActivePopup();
      if (this.activeStopEditing) {
        this.activeStopEditing();
        this.activeStopEditing = null;
      }

      paletteContainer = new Container();
      paletteContainer.eventMode = "static";
      paletteContainer.zIndex = 100000;

      // hide transparent option for text-related color props (text, heading, accent, etc.)
      const propKey = prop.key.toLowerCase();
      const allowTransparent = propKey.includes("bg") || propKey.includes("border");
      const palette = allowTransparent
        ? COLOR_PALETTE
        : COLOR_PALETTE.filter((c) => c !== TRANSPARENT_COLOR);

      const palettePad = 6;
      // +1 for the custom "pick any color" swatch appended after the palette loop
      const rows = Math.ceil((palette.length + 1) / PALETTE_COLS);
      const paletteW = PALETTE_COLS * (SWATCH_SIZE + SWATCH_GAP) - SWATCH_GAP + palettePad * 2;
      const paletteH = rows * (SWATCH_SIZE + SWATCH_GAP) - SWATCH_GAP + palettePad * 2;

      // palette background
      const palBg = new Graphics();
      palBg.roundRect(0, 0, paletteW, paletteH, 6);
      palBg.fill({ color: this.theme.toolbarBg, alpha: 0.98 });
      palBg.stroke({ color: this.theme.toolbarBorder, width: 1 });
      palBg.eventMode = "static";
      palBg.on("pointerdown", (e: FederatedPointerEvent) => e.stopPropagation());
      paletteContainer.addChild(palBg);

      // color swatches
      for (let i = 0; i < palette.length; i++) {
        const col = i % PALETTE_COLS;
        const row = Math.floor(i / PALETTE_COLS);
        const color = palette[i];

        const sw = new Graphics();
        const sx = palettePad + col * (SWATCH_SIZE + SWATCH_GAP);
        const sy = palettePad + row * (SWATCH_SIZE + SWATCH_GAP);

        const drawPaletteSwatch = (g: Graphics, active: boolean) => {
          g.clear();
          if (color === TRANSPARENT_COLOR) {
            const half = Math.floor(SWATCH_SIZE / 2);
            // light squares
            g.rect(sx, sy, half, half);
            g.fill({ color: 0xcccccc });
            g.rect(sx + half, sy + half, SWATCH_SIZE - half, SWATCH_SIZE - half);
            g.fill({ color: 0xcccccc });
            // dark squares
            g.rect(sx + half, sy, SWATCH_SIZE - half, half);
            g.fill({ color: 0x888888 });
            g.rect(sx, sy + half, half, SWATCH_SIZE - half);
            g.fill({ color: 0x888888 });
            // border
            g.roundRect(sx, sy, SWATCH_SIZE, SWATCH_SIZE, 3);
            g.stroke({ color: active ? this.theme.accent : 0x555555, width: active ? 2 : 1 });
          } else {
            g.roundRect(sx, sy, SWATCH_SIZE, SWATCH_SIZE, 3);
            g.fill({ color });
            if (active) {
              g.stroke({ color: this.theme.accent, width: 2 });
            } else if (color >= 0xd0d0d0) {
              g.stroke({ color: 0x555555, width: 1 });
            }
          }
        };
        drawPaletteSwatch(sw, false);

        sw.eventMode = "static";
        sw.cursor = "pointer";

        // highlight on hover
        sw.on("pointerover", () => drawPaletteSwatch(sw, true));
        sw.on("pointerout", () => drawPaletteSwatch(sw, false));

        sw.on("pointerdown", (e: FederatedPointerEvent) => {
          e.stopPropagation();
          currentColor = color;
          hexText.text = formatHex(currentColor);
          drawSwatch();
          onChange(currentColor);
          closePalette();
        });

        paletteContainer.addChild(sw);
      }

      // ── custom color picker swatch ─────────────────────────────────────────
      // placed at the next grid position after all palette swatches.
      // clicking it opens the native system color picker via a hidden <input>.
      {
        const customIdx = palette.length;
        const customCol = customIdx % PALETTE_COLS;
        const customRow = Math.floor(customIdx / PALETTE_COLS);
        const sx = palettePad + customCol * (SWATCH_SIZE + SWATCH_GAP);
        const sy = palettePad + customRow * (SWATCH_SIZE + SWATCH_GAP);
        const half = Math.floor(SWATCH_SIZE / 2);

        const csw = new Graphics();
        const drawCustomSwatch = (active: boolean) => {
          csw.clear();
          // 4-quadrant rainbow fill to signal "any color"
          csw.rect(sx, sy, half, half);
          csw.fill({ color: 0xff5555 });
          csw.rect(sx + half, sy, SWATCH_SIZE - half, half);
          csw.fill({ color: 0x55cc55 });
          csw.rect(sx, sy + half, half, SWATCH_SIZE - half);
          csw.fill({ color: 0x5588ff });
          csw.rect(sx + half, sy + half, SWATCH_SIZE - half, SWATCH_SIZE - half);
          csw.fill({ color: 0xcc55cc });
          csw.roundRect(sx, sy, SWATCH_SIZE, SWATCH_SIZE, 3);
          csw.stroke({ color: active ? this.theme.accent : 0x555555, width: active ? 2 : 1 });
        };
        drawCustomSwatch(false);

        csw.eventMode = "static";
        csw.cursor = "pointer";
        csw.on("pointerover", () => drawCustomSwatch(true));
        csw.on("pointerout", () => drawCustomSwatch(false));

        csw.on("pointerdown", (e: FederatedPointerEvent) => {
          e.stopPropagation();

          // remove any previously-created input that didn't get cleaned up
          if (liveColorInput && document.body.contains(liveColorInput)) {
            document.body.removeChild(liveColorInput);
          }

          const input = document.createElement("input");
          input.type = "color";
          if (currentColor !== TRANSPARENT_COLOR) {
            input.value = "#" + (currentColor & 0xffffff).toString(16).padStart(6, "0");
          }

          // position a real-sized input element over the swatch using the
          // same getBoundingClientRect approach as dom-overlay / skein-input.
          // a zero-size or fully-hidden input won't open the native picker
          // reliably in WKWebView (Tauri), so we give it real dimensions at
          // the swatch's screen position.
          const swatchGlobal = csw.toGlobal({ x: sx, y: sy });
          const canvasRect = this.canvasElement.getBoundingClientRect();
          const screenX = canvasRect.left + swatchGlobal.x;
          const screenY = canvasRect.top + swatchGlobal.y;

          input.style.cssText = [
            "position:fixed",
            `left:${screenX}px`,
            `top:${screenY}px`,
            `width:${SWATCH_SIZE}px`,
            `height:${SWATCH_SIZE}px`,
            "opacity:0.001",
            "border:none",
            "padding:0",
            "z-index:10001",
          ].join(";");

          liveColorInput = input;
          document.body.appendChild(input);

          // update color on both input and change — macOS WKWebView fires
          // "change" on every slider interaction (not just on dismissal), so
          // treating either event as a "final close" signal would close the
          // picker immediately on slider click.
          // closure happens entirely through closePalette() which is called
          // when the user clicks elsewhere (tray root pointerdown handler).
          const updateColor = () => {
            const hex = parseInt(input.value.slice(1), 16);
            currentColor = hex;
            hexText.text = formatHex(currentColor);
            drawSwatch();
            onChange(currentColor);
          };
          input.addEventListener("input", updateColor);
          input.addEventListener("change", updateColor);

          // requestAnimationFrame ensures the input is laid out before we
          // call showPicker() — without this the first click does nothing
          // in WKWebView (Tauri) because the element isn't yet in the render tree.
          requestAnimationFrame(() => {
            const opened = (input as any).showPicker
              ? (() => {
                  try {
                    (input as any).showPicker();
                    return true;
                  } catch {
                    return false;
                  }
                })()
              : false;
            if (!opened) input.click();
          });
        });

        paletteContainer.addChild(csw);
      }

      // position below the field, accounting for the control's offset in the tray
      paletteContainer.x = TRAY_PAD_H;
      paletteContainer.y = container.y + this.contentContainer.y + fieldY + FIELD_HEIGHT + 4;

      this.root.addChild(paletteContainer);
      this.activePopupClose = closePalette;
    };

    // toggle palette on click
    fieldBg.eventMode = "static";
    fieldBg.cursor = "pointer";
    fieldBg.on("pointerdown", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      if (paletteContainer) {
        closePalette();
      } else {
        openPalette();
      }
    });

    const totalHeight = fieldY + FIELD_HEIGHT;

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(value: unknown) {
        currentColor = Number(value) || 0;
        hexText.text = formatHex(currentColor);
        drawSwatch();
      },
      setWidth(fw: number) {
        currentFieldWidth = fw;
        drawFieldLayout();
      },
      destroy() {
        closePalette();
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // select control
  // ---------------------------------------------------------------------------

  private createSelectControl(
    prop: WidgetPropDef,
    initialValue: string,
    onChange: (value: string) => void,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    // options may be a static array or a function (called fresh on each open)
    const resolveOptions = (): string[] =>
      typeof prop.options === "function"
        ? (prop.options as () => string[])()
        : (prop.options ?? []);

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);
    let currentFieldWidth = fieldWidth;
    let currentValue = initialValue;

    // field background
    const fieldBg = new Graphics();
    container.addChild(fieldBg);

    // text mask for clipping
    const fieldTextMask = new Graphics();
    container.addChild(fieldTextMask);

    // display the current value (truncated to show just the primary font name)
    const displayText = new Text({
      text: formatSelectLabel(currentValue),
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: this.theme.frameHeaderText,
      },
    });
    displayText.x = 6;
    displayText.y = fieldY + Math.round((FIELD_HEIGHT - displayText.height) / 2);
    displayText.mask = fieldTextMask;
    displayText.eventMode = "none";
    container.addChild(displayText);

    // dropdown arrow
    const arrow = new Text({
      text: "\u25BE",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall - 1,
        fill: 0x808080,
      },
    });
    arrow.eventMode = "none";
    container.addChild(arrow);

    let dropdownContainer: Container | null = null;

    const drawFieldLayout = () => {
      this.drawFieldBg(fieldBg, fieldY, currentFieldWidth, false);
      fieldTextMask.clear();
      fieldTextMask.rect(4, fieldY + 1, currentFieldWidth - 24, FIELD_HEIGHT - 2);
      fieldTextMask.fill({ color: 0xffffff });
      arrow.x = currentFieldWidth - 16;
      arrow.y = fieldY + Math.round((FIELD_HEIGHT - arrow.height) / 2);
    };
    drawFieldLayout();

    const closeDropdown = () => {
      if (dropdownContainer) {
        if (dropdownContainer.parent) {
          dropdownContainer.parent.removeChild(dropdownContainer);
        }
        dropdownContainer.destroy({ children: true });
        dropdownContainer = null;
      }
      if (this.activePopupClose === closeDropdown) {
        this.activePopupClose = null;
      }
    };

    const openDropdown = () => {
      this.closeActivePopup();
      if (this.activeStopEditing) {
        this.activeStopEditing();
        this.activeStopEditing = null;
      }

      const options = resolveOptions();
      dropdownContainer = new Container();
      dropdownContainer.eventMode = "static";
      dropdownContainer.zIndex = 100000;

      const itemHeight = FIELD_HEIGHT;
      const itemPadH = 6;
      const ddPad = 4;
      const ddWidth = currentFieldWidth;
      const ddHeight = options.length * itemHeight + ddPad * 2;

      // dropdown background
      const ddBg = new Graphics();
      ddBg.roundRect(0, 0, ddWidth, ddHeight, 4);
      ddBg.fill({ color: this.theme.toolbarBg, alpha: 0.98 });
      ddBg.stroke({ color: this.theme.toolbarBorder, width: 1 });
      ddBg.eventMode = "static";
      ddBg.on("pointerdown", (e: FederatedPointerEvent) => e.stopPropagation());
      dropdownContainer.addChild(ddBg);

      // option items
      for (let i = 0; i < options.length; i++) {
        const option = options[i];
        const itemY = ddPad + i * itemHeight;

        const itemBg = new Graphics();
        const isSelected = option === currentValue;
        itemBg.roundRect(ddPad, itemY, ddWidth - ddPad * 2, itemHeight, 3);
        itemBg.fill({
          color: isSelected ? this.theme.accent : 0x000000,
          alpha: isSelected ? 0.15 : 0,
        });
        itemBg.eventMode = "static";
        itemBg.cursor = "pointer";
        dropdownContainer.addChild(itemBg);

        const itemText = new Text({
          text: formatSelectLabel(option),
          resolution: this.theme.textResolution,
          style: {
            fontFamily: this.theme.fontFamily,
            fontSize: this.theme.fontSizeSmall,
            fill: isSelected ? this.theme.accent : this.theme.frameHeaderText,
          },
        });
        itemText.x = ddPad + itemPadH;
        itemText.y = itemY + Math.round((itemHeight - itemText.height) / 2);
        itemText.eventMode = "none";
        dropdownContainer.addChild(itemText);

        itemBg.on("pointerover", () => {
          itemBg.clear();
          itemBg.roundRect(ddPad, itemY, ddWidth - ddPad * 2, itemHeight, 3);
          itemBg.fill({ color: this.theme.accent, alpha: 0.15 });
        });
        itemBg.on("pointerout", () => {
          itemBg.clear();
          itemBg.roundRect(ddPad, itemY, ddWidth - ddPad * 2, itemHeight, 3);
          const sel = option === currentValue;
          itemBg.fill({ color: sel ? this.theme.accent : 0x000000, alpha: sel ? 0.15 : 0 });
        });

        itemBg.on("pointerdown", (e: FederatedPointerEvent) => {
          e.stopPropagation();
          currentValue = option;
          displayText.text = formatSelectLabel(currentValue);
          onChange(currentValue);
          closeDropdown();
        });
      }

      // position below the field, accounting for the control's offset in the tray
      dropdownContainer.x = TRAY_PAD_H;
      dropdownContainer.y = container.y + this.contentContainer.y + fieldY + FIELD_HEIGHT + 4;

      this.root.addChild(dropdownContainer);
      this.activePopupClose = closeDropdown;
    };

    // toggle dropdown on click
    fieldBg.eventMode = "static";
    fieldBg.cursor = "pointer";
    fieldBg.on("pointerdown", (e: FederatedPointerEvent) => {
      e.stopPropagation();
      if (dropdownContainer) {
        closeDropdown();
      } else {
        openDropdown();
      }
    });

    const totalHeight = fieldY + FIELD_HEIGHT;

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(value: unknown) {
        currentValue = String(value ?? "");
        displayText.text = formatSelectLabel(currentValue);
      },
      setWidth(fw: number) {
        currentFieldWidth = fw;
        drawFieldLayout();
      },
      destroy() {
        closeDropdown();
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // image control
  // ---------------------------------------------------------------------------

  private createImageControl(
    prop: WidgetPropDef,
    initialValue: string,
    onChange: (value: string) => void,
    fieldWidth: number
  ): PropControl {
    const container = new Container();
    container.eventMode = "static";

    const label = this.createLabel(prop.label);
    container.addChild(label);

    const fieldY = Math.round(label.height + LABEL_FIELD_GAP);

    // preview area — shows the current image or a placeholder
    const PREVIEW_HEIGHT = 60;
    const previewBg = new Graphics();
    container.addChild(previewBg);

    let previewSprite: Sprite | null = null;
    let currentDataUrl = initialValue;

    const drawPreview = (fw: number) => {
      previewBg.clear();
      previewBg.roundRect(0, fieldY, fw, PREVIEW_HEIGHT, 4);
      previewBg.fill({ color: 0x141414 });
      previewBg.stroke({ color: this.theme.frameBorder, width: 1 });

      if (previewSprite) {
        // fit the sprite within the preview area with padding
        const pad = 4;
        const maxW = fw - pad * 2;
        const maxH = PREVIEW_HEIGHT - pad * 2;
        const scale = Math.min(
          maxW / previewSprite.texture.width,
          maxH / previewSprite.texture.height,
          1
        );
        previewSprite.width = previewSprite.texture.width * scale;
        previewSprite.height = previewSprite.texture.height * scale;
        previewSprite.x = pad + (maxW - previewSprite.width) / 2;
        previewSprite.y = fieldY + pad + (maxH - previewSprite.height) / 2;
      }
    };

    const updateSprite = async (dataUrl: string) => {
      if (previewSprite) {
        container.removeChild(previewSprite);
        previewSprite.destroy();
        previewSprite = null;
      }
      // NOTE: do NOT call Assets.unload() here. the property tray is a consumer
      // of textures, not an owner. the widget that loaded this texture still
      // references it — unloading here would destroy a shared asset and crash
      // the renderer (alphaMode null error in StencilMaskPipe).
      if (!dataUrl) {
        drawPreview(fieldWidth);
        return;
      }
      try {
        // dataUrl may be a blob:<id> reference (see file-utils/image-prop-blob) —
        // resolve it to an object URL before handing it to Assets.load; a legacy
        // raw data: URL or external URL passes through unchanged.
        const loadUrl = await resolveImagePropUrl(dataUrl);
        if (!loadUrl) {
          drawPreview(fieldWidth);
          return;
        }
        const texture = await Assets.load<Texture>(loadUrl);
        // race check: if another load started while we were loading, bail out
        if (currentDataUrl !== dataUrl) return;
        previewSprite = new Sprite(texture);
        previewSprite.eventMode = "none";
        container.addChild(previewSprite);
        drawPreview(fieldWidth);
      } catch {
        // silently ignore load failures
      }
    };

    if (initialValue) {
      updateSprite(initialValue);
    }

    // buttons row below the preview
    const BTN_HEIGHT = 22;
    const BTN_GAP = 4;
    const btnY = fieldY + PREVIEW_HEIGHT + BTN_GAP;

    const uploadBtn = new Container();
    uploadBtn.eventMode = "static";
    uploadBtn.cursor = "pointer";
    const uploadBg = new Graphics();
    uploadBtn.addChild(uploadBg);
    const uploadText = new Text({
      text: currentDataUrl ? "change" : "upload",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall - 1,
        fill: 0xffffff,
      },
    });
    uploadText.eventMode = "none";
    uploadBtn.addChild(uploadText);
    container.addChild(uploadBtn);

    // optional clear button (only shown when there's an image)
    const clearBtn = new Container();
    clearBtn.eventMode = "static";
    clearBtn.cursor = "pointer";
    const clearBg = new Graphics();
    clearBtn.addChild(clearBg);
    const clearText = new Text({
      text: "clear",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall - 1,
        fill: 0xef4444,
      },
    });
    clearText.eventMode = "none";
    clearBtn.addChild(clearText);
    container.addChild(clearBtn);

    const layoutButtons = (fw: number) => {
      const hasImage = !!currentDataUrl;
      clearBtn.visible = hasImage;
      uploadText.text = hasImage ? "change" : "upload";

      // upload button
      const uploadW = hasImage ? Math.floor((fw - BTN_GAP) / 2) : fw;
      uploadBg.clear();
      uploadBg.roundRect(0, 0, uploadW, BTN_HEIGHT, 3);
      uploadBg.fill({ color: 0x1a1a2e });
      uploadBg.stroke({ color: this.theme.frameBorder, width: 1 });
      uploadText.x = Math.round((uploadW - uploadText.width) / 2);
      uploadText.y = Math.round((BTN_HEIGHT - uploadText.height) / 2);
      uploadBtn.x = 0;
      uploadBtn.y = btnY;

      // clear button
      if (hasImage) {
        const clearW = fw - uploadW - BTN_GAP;
        clearBg.clear();
        clearBg.roundRect(0, 0, clearW, BTN_HEIGHT, 3);
        clearBg.fill({ color: 0x1a1a2e });
        clearBg.stroke({ color: 0x4a2020, width: 1 });
        clearText.x = Math.round((clearW - clearText.width) / 2);
        clearText.y = Math.round((BTN_HEIGHT - clearText.height) / 2);
        clearBtn.x = uploadW + BTN_GAP;
        clearBtn.y = btnY;
      }
    };

    layoutButtons(fieldWidth);
    drawPreview(fieldWidth);

    // upload click handler
    uploadBtn.on("pointertap", async (e: any) => {
      e.stopPropagation();
      this.closeActivePopup();
      const dataUrl = await pickImageOrGifAsDataUrl({
        maxWidth: prop.imageMaxWidth ?? 320,
        maxHeight: prop.imageMaxHeight ?? 200,
        quality: 0.8,
        cropSquare: prop.imageCropSquare ?? false,
      });
      if (dataUrl) {
        // show the freshly picked image immediately — no need to round-trip
        // through the blob store just to preview what we already have in hand.
        currentDataUrl = dataUrl;
        updateSprite(dataUrl);
        layoutButtons(fieldWidth);
        try {
          const ref = await saveImageDataUrlAsBlobRef(dataUrl);
          currentDataUrl = ref;
          onChange(ref);
        } catch (err) {
          log.warn("property-tray", "failed to persist image as blob ref, storing raw data url:", err);
          onChange(dataUrl);
        }
      }
    });

    // clear click handler
    clearBtn.on("pointertap", (e: any) => {
      e.stopPropagation();
      this.closeActivePopup();
      currentDataUrl = "";
      onChange("");
      updateSprite("");
      layoutButtons(fieldWidth);
    });

    const totalHeight = btnY + BTN_HEIGHT;

    return {
      key: prop.key,
      height: totalHeight,
      container,
      update(value: unknown) {
        const v = String(value ?? "");
        if (v !== currentDataUrl) {
          currentDataUrl = v;
          updateSprite(v);
          layoutButtons(fieldWidth);
        }
      },
      setWidth(fw: number) {
        fieldWidth = fw;
        drawPreview(fw);
        layoutButtons(fw);
      },
      destroy() {
        if (previewSprite) {
          container.removeChild(previewSprite);
          previewSprite.destroy();
          previewSprite = null;
        }
        // NOTE: do NOT call Assets.unload() here. the property tray is a consumer
        // of textures, not an owner. the widget that loaded this texture still
        // references it — unloading here would destroy a shared asset and crash
        // the renderer (alphaMode null error in StencilMaskPipe).
        container.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // compact-doc button
  // ---------------------------------------------------------------------------

  /**
   * "compact this doc" — gives `widgetId`'s per-widget automerge doc a
   * fresh, history-free identity seeded from its current state (see
   * `compact-doc.ts` and `docs/animaniac-doc-compaction-plan.md`).
   * automerge's op-log is append-only, so this is the only way to actually
   * shrink a doc that's accumulated a lot of history.
   *
   * `docHandle` is `null` for stateless widgets or a non-automerge doc
   * override (e.g. SqliteSocialDoc) — in that case, and for a doc that
   * isn't ready yet, this returns an empty container (no button shown),
   * mirroring `createDeleteButton`'s isSingleton early-return.
   *
   * the op-count label uses `computeDocHistoryStats()`'s cheap
   * (`A.stats()`-only) path — never the expensive decode-loop — since this
   * doc is already resident (mounted) and must stay cheap to display.
   *
   * only shown once the doc crosses `COMPACT_CHANGES_THRESHOLD` (see
   * `shouldOfferCompaction()`) — a fresh/small doc doesn't need compacting,
   * and offering the button anyway just invites users to compact docs that
   * don't need it (each compaction gives the doc a new identity, forcing a
   * full resync for every other peer).
   */
  private createCompactButton(
    widgetId: string,
    docHandle: DocHandle<any> | null,
    fieldWidth: number
  ): { container: Container; height: number } {
    const container = new Container();
    if (!docHandle) return { container, height: 0 };

    const stats = computeDocHistoryStats(docHandle);
    if (!stats) return { container, height: 0 };
    if (!shouldOfferCompaction(stats)) return { container, height: 0 };

    const btnHeight = FIELD_HEIGHT;
    const statsLineHeight = 14;

    const statsText = new Text({
      text: `history: ${formatCompactCount(stats.numChanges)} changes, ${formatCompactCount(stats.numOps)} ops`,
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: 10,
        fill: 0x9999aa,
      },
    });
    statsText.eventMode = "none";
    statsText.y = 0;
    container.addChild(statsText);

    const buttonY = statsLineHeight + 2;

    const compactBg = new Graphics();
    const compactText = new Text({
      text: "compact this doc",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xffffff,
      },
    });
    compactText.anchor.set(0.5, 0.5);
    compactText.eventMode = "none";

    const confirmContainer = new Container();
    confirmContainer.visible = false;

    const confirmText = new Text({
      text: "give it a fresh, history-free copy?",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xaaaaaa,
        wordWrap: true,
        wordWrapWidth: fieldWidth,
        align: "center",
      },
    });
    confirmText.eventMode = "none";

    const yesBg = new Graphics();
    const yesText = new Text({
      text: "yes",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xffffff,
      },
    });
    yesText.anchor.set(0.5, 0.5);
    yesText.eventMode = "none";

    const cancelBg = new Graphics();
    const cancelText = new Text({
      text: "cancel",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xaaaaaa,
      },
    });
    cancelText.anchor.set(0.5, 0.5);
    cancelText.eventMode = "none";

    const drawCompact = () => {
      compactBg.clear();
      compactBg.roundRect(0, 0, fieldWidth, btnHeight, 4);
      compactBg.fill({ color: 0x1e3a5f });
      compactText.x = fieldWidth / 2;
      compactText.y = btnHeight / 2;
    };

    // confirm row sits ABOVE the yes/cancel buttons (two lines total),
    // unlike createDeleteButton's single-line confirm — this message is
    // longer, so it wraps and needs its own row height.
    const confirmTextHeight = 28;

    const drawConfirm = () => {
      const halfW = (fieldWidth - 4) / 2;

      confirmText.x = fieldWidth / 2;
      confirmText.y = 0;
      confirmText.anchor.set(0.5, 0);

      const rowY = confirmTextHeight;

      yesBg.clear();
      yesBg.roundRect(0, 0, halfW, btnHeight, 4);
      yesBg.fill({ color: 0x1d4ed8 });
      yesText.x = halfW / 2;
      yesText.y = btnHeight / 2;

      cancelBg.clear();
      cancelBg.roundRect(0, 0, halfW, btnHeight, 4);
      cancelBg.fill({ color: 0x374151 });
      cancelText.x = halfW / 2;
      cancelText.y = btnHeight / 2;

      const yesContainer = new Container();
      yesContainer.addChild(yesBg, yesText);
      yesContainer.x = 0;
      yesContainer.y = rowY;

      const cancelContainer2 = new Container();
      cancelContainer2.addChild(cancelBg, cancelText);
      cancelContainer2.x = halfW + 4;
      cancelContainer2.y = rowY;

      confirmContainer.removeChildren();
      confirmContainer.addChild(confirmText, yesContainer, cancelContainer2);
    };

    drawCompact();
    const buttonContainer = new Container();
    buttonContainer.y = buttonY;
    buttonContainer.addChild(compactBg, compactText);
    container.addChild(buttonContainer);

    compactBg.eventMode = "static";
    compactBg.cursor = "pointer";
    compactBg.on("pointertap", (e) => {
      e.stopPropagation();
      buttonContainer.visible = false;
      drawConfirm();
      confirmContainer.visible = true;
      confirmContainer.y = buttonY;
      container.addChild(confirmContainer);
      // the confirm block is taller than the plain button (two lines vs
      // one) — without this, whatever's below (the delete button) stays
      // at its old y and the confirm's own yes/cancel row ends up
      // overlapping/hidden behind it.
      this.relayoutControls();
    });

    yesBg.eventMode = "static";
    yesBg.cursor = "pointer";
    yesBg.on("pointertap", (e) => {
      e.stopPropagation();
      yesBg.cursor = "default";
      yesText.text = "working\u2026";
      void (async () => {
        try {
          // if any other peer is currently online, give them a brief
          // window to relay any last-moment changes before snapshotting --
          // especially relevant when a hub is relaying on behalf of a
          // peer that's still offline. best-effort only (see
          // waitForQuietPeriod's own doc comment); skipped entirely when
          // solo, since there's nothing to wait for.
          const onlinePeerIds = Object.keys(this.store.peers()).filter(
            (id) => id !== this.store.localNodeId && this.store.isPeerOnline(id)
          );
          if (onlinePeerIds.length > 0) {
            yesText.text = "waiting for peers\u2026";
            await waitForQuietPeriod(docHandle);
            yesText.text = "working\u2026";
          }

          const result = await compactDoc(this.store.repo, docHandle);
          if (!result) {
            log.warn("property-tray", `compaction failed for widget ${widgetId} (doc ${docHandle.documentId})`);
            yesText.text = "failed";
            return;
          }
          this.store.setDocId(widgetId, result.newHandle.documentId);
          // the canvas doc change above flows through reconcile()'s
          // docId-change check, which unmounts/remounts this widget — this
          // tray will be rebuilt from scratch against the new doc shortly,
          // so no further UI update is needed here.
        } catch (err) {
          log.warn("property-tray", `compaction threw for widget ${widgetId}:`, err);
          yesText.text = "failed";
        }
      })();
    });

    cancelBg.eventMode = "static";
    cancelBg.cursor = "pointer";
    cancelBg.on("pointertap", (e) => {
      e.stopPropagation();
      confirmContainer.visible = false;
      buttonContainer.visible = true;
      this.relayoutControls();
    });

    return { container, height: buttonY + btnHeight };
  }

  // ---------------------------------------------------------------------------
  // delete button
  // ---------------------------------------------------------------------------

  private createDeleteButton(
    widgetId: string,
    isSingleton: boolean,
    fieldWidth: number
  ): Container {
    const container = new Container();
    if (isSingleton) return container;

    const btnHeight = FIELD_HEIGHT;

    const deleteBg = new Graphics();
    const deleteText = new Text({
      text: "delete widget",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xffffff,
      },
    });
    deleteText.anchor.set(0.5, 0.5);
    deleteText.eventMode = "none";

    // confirmation elements
    const confirmContainer = new Container();
    confirmContainer.visible = false;

    const confirmText = new Text({
      text: "are you sure?",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xaaaaaa,
      },
    });
    confirmText.eventMode = "none";

    // "yes" button
    const yesBg = new Graphics();
    const yesText = new Text({
      text: "yes",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xffffff,
      },
    });
    yesText.anchor.set(0.5, 0.5);
    yesText.eventMode = "none";

    // "cancel" button
    const cancelBg = new Graphics();
    const cancelText = new Text({
      text: "cancel",
      resolution: this.theme.textResolution,
      style: {
        fontFamily: this.theme.fontFamily,
        fontSize: this.theme.fontSizeSmall,
        fill: 0xaaaaaa,
      },
    });
    cancelText.anchor.set(0.5, 0.5);
    cancelText.eventMode = "none";

    const drawDelete = () => {
      deleteBg.clear();
      deleteBg.roundRect(0, 0, fieldWidth, btnHeight, 4);
      deleteBg.fill({ color: 0x7f1d1d });
      deleteText.x = fieldWidth / 2;
      deleteText.y = btnHeight / 2;
    };

    const drawConfirm = () => {
      const halfW = (fieldWidth - 4) / 2;

      confirmText.x = fieldWidth / 2;
      confirmText.y = -2;
      confirmText.anchor.set(0.5, 1);

      yesBg.clear();
      yesBg.roundRect(0, 0, halfW, btnHeight, 4);
      yesBg.fill({ color: 0xb91c1c });
      yesText.x = halfW / 2;
      yesText.y = btnHeight / 2;

      cancelBg.clear();
      cancelBg.roundRect(0, 0, halfW, btnHeight, 4);
      cancelBg.fill({ color: 0x374151 });
      cancelText.x = halfW / 2;
      cancelText.y = btnHeight / 2;

      const yesContainer = new Container();
      yesContainer.addChild(yesBg, yesText);
      yesContainer.x = 0;

      const cancelContainer2 = new Container();
      cancelContainer2.addChild(cancelBg, cancelText);
      cancelContainer2.x = halfW + 4;

      confirmContainer.removeChildren();
      confirmContainer.addChild(confirmText, yesContainer, cancelContainer2);
    };

    drawDelete();
    container.addChild(deleteBg, deleteText);

    // click: show confirmation
    deleteBg.eventMode = "static";
    deleteBg.cursor = "pointer";
    deleteBg.on("pointertap", (e) => {
      e.stopPropagation();
      deleteBg.visible = false;
      deleteText.visible = false;
      drawConfirm();
      confirmContainer.visible = true;
      container.addChild(confirmContainer);
    });

    // yes = delete
    yesBg.eventMode = "static";
    yesBg.cursor = "pointer";
    yesBg.on("pointertap", (e) => {
      e.stopPropagation();
      this.widgetManager.closeWidget(widgetId);
    });

    // cancel = restore
    cancelBg.eventMode = "static";
    cancelBg.cursor = "pointer";
    cancelBg.on("pointertap", (e) => {
      e.stopPropagation();
      confirmContainer.visible = false;
      deleteBg.visible = true;
      deleteText.visible = true;
    });

    return container;
  }
}

// ---------------------------------------------------------------------------
// utilities
// ---------------------------------------------------------------------------

/**
 * format a number as a hex color string: #rrggbb
 */
function formatHex(color: number): string {
  if (color === TRANSPARENT_COLOR) return "none";
  return "#" + (color & 0xffffff).toString(16).padStart(6, "0");
}

/**
 * format a select option value for display. for CSS font stacks like
 * "system-ui, sans-serif", show just the first family name.
 */
function formatSelectLabel(value: string): string {
  const first = value.split(",")[0].trim();
  return first || value;
}

/**
 * abbreviate a large count for display (176990 -> "177k", 2400000 -> "2.4M") --
 * used by the compact-doc button's history stats line, where the exact
 * figure matters far less than the rough order of magnitude. values under
 * 1000 are shown as-is.
 */
function formatCompactCount(n: number): string {
  if (n < 1_000) return String(n);
  if (n < 1_000_000) return `${Math.round(n / 1_000)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
}
